import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid } from "recharts";

export default function Metrics() {
  const [logs, setLogs] = useState([]);
  const [form, setForm] = useState({ weight_kg: "", body_fat_pct: "", sleep_hours: "", note: "" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/metrics").then((r) => setLogs(r.data));
  useEffect(() => {
    load();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = Object.fromEntries(
        Object.entries(form).map(([k, v]) => [k, v === "" ? null : k === "note" ? v : parseFloat(v)])
      );
      await api.post("/metrics/log", body);
      toast.success("Log recorded");
      setForm({ weight_kg: "", body_fat_pct: "", sleep_hours: "", note: "" });
      load();
    } catch {
      toast.error("Failed to record");
    } finally {
      setBusy(false);
    }
  };

  const chartData = logs
    .slice()
    .reverse()
    .map((l) => ({
      day: l.day.slice(5),
      weight: l.weight_kg,
      bf: l.body_fat_pct,
      sleep: l.sleep_hours,
    }));

  return (
    <div className="space-y-6" data-testid="metrics-page">
      <div>
        <div className="label-tech">Vital Metrics</div>
        <h1 className="h-display text-3xl sm:text-4xl mt-1">
          Body <span className="text-violet-400">System</span>
        </h1>
      </div>

      {/* Log form */}
      <form onSubmit={submit} className="panel panel-corners p-6 grid grid-cols-1 sm:grid-cols-4 gap-3">
        {[
          { key: "weight_kg", label: "Weight (kg)", step: "0.1" },
          { key: "body_fat_pct", label: "Body Fat %", step: "0.1" },
          { key: "sleep_hours", label: "Sleep (hrs)", step: "0.25" },
        ].map((f) => (
          <div key={f.key}>
            <label className="label-tech block mb-1.5">{f.label}</label>
            <input
              data-testid={`metric-${f.key}`}
              type="number"
              step={f.step}
              value={form[f.key]}
              onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
              className="w-full bg-black/60 border border-white/10 focus:border-cyan-400/60 outline-none px-3 py-2 text-white font-mono-tech text-sm"
            />
          </div>
        ))}
        <div className="flex items-end">
          <button data-testid="metric-submit" disabled={busy} className="btn-neon w-full">
            {busy ? "..." : "Log"}
          </button>
        </div>
        <div className="sm:col-span-4">
          <label className="label-tech block mb-1.5">Note</label>
          <input
            data-testid="metric-note"
            value={form.note}
            onChange={(e) => setForm({ ...form, note: e.target.value })}
            className="w-full bg-black/60 border border-white/10 focus:border-cyan-400/60 outline-none px-3 py-2 text-white text-sm"
          />
        </div>
      </form>

      {/* Chart */}
      {chartData.length > 1 && (
        <div className="panel panel-corners p-6">
          <div className="label-tech mb-3">Trend</div>
          <div className="h-64">
            <ResponsiveContainer>
              <LineChart data={chartData}>
                <CartesianGrid stroke="rgba(139,92,246,0.15)" strokeDasharray="3 3" />
                <XAxis dataKey="day" stroke="#64748b" fontSize={11} />
                <YAxis stroke="#64748b" fontSize={11} />
                <Tooltip contentStyle={{ background: "#0F0F16", border: "1px solid #00E5FF" }} />
                <Line type="monotone" dataKey="weight" stroke="#00E5FF" strokeWidth={2} dot={false} name="Weight" />
                <Line type="monotone" dataKey="bf" stroke="#8B5CF6" strokeWidth={2} dot={false} name="BF %" />
                <Line type="monotone" dataKey="sleep" stroke="#10B981" strokeWidth={2} dot={false} name="Sleep" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Logs table */}
      <div className="panel panel-corners p-6">
        <div className="label-tech mb-3">Recent Logs</div>
        {logs.length === 0 ? (
          <p className="text-sm text-slate-500">No logs yet. Record your first vital reading above.</p>
        ) : (
          <div className="space-y-2">
            {logs.map((l) => (
              <div key={l.id} className="grid grid-cols-5 gap-2 text-sm border-b border-white/5 py-2">
                <div className="font-mono-tech text-xs text-cyan-400">{l.day}</div>
                <div>{l.weight_kg != null ? `${l.weight_kg} kg` : "—"}</div>
                <div>{l.body_fat_pct != null ? `${l.body_fat_pct}%` : "—"}</div>
                <div>{l.sleep_hours != null ? `${l.sleep_hours}h` : "—"}</div>
                <div className="text-slate-500 text-xs truncate">{l.note || ""}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
